type WidgetConfigSource = mumms.WidgetConfigApiItem | mumms.WidgetConfig;

function parseApiJsonField<T>(value: string | null | undefined): T | null {
  if (value == null || value === '') {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function isWidgetConfigApiItem(
  item: WidgetConfigSource,
): item is mumms.WidgetConfigApiItem {
  return 'dashboard' in item || 'groupType' in item;
}

function toWidgetConfig(item: WidgetConfigSource): mumms.WidgetConfig {
  return isWidgetConfigApiItem(item) ? normalizeApiWidget(item) : item;
}

function layoutComponentNames(
  layout: WidgetConfigSource[] | undefined,
): Set<mumms.ComponentName> {
  return new Set(
    (layout ?? [])
      .map((w) => w.componentName)
      .filter((name): name is mumms.ComponentName => Boolean(name)),
  );
}

/**
 * Dashboard menu sort: non-`doNotShow` first, then `doNotShow` last. Within each
 * group: finite numeric `widgetMenuOrder` ascending; missing or non-numeric last
 * (stable among ties — e.g. merged `config` before `extras` when both lack order).
 */
export function compareWidgetMenuOrder(
  a: mumms.WidgetConfig,
  b: mumms.WidgetConfig,
): number {
  const aHide = Boolean(a.doNotShow);
  const bHide = Boolean(b.doNotShow);
  if (aHide !== bHide) {
    return aHide ? 1 : -1;
  }
  const ra =
    a.widgetMenuOrder == null ? Number.POSITIVE_INFINITY : a.widgetMenuOrder;
  const rb =
    b.widgetMenuOrder == null ? Number.POSITIVE_INFINITY : b.widgetMenuOrder;
  return ra - rb;
}

/**
 * Sort by {@link compareWidgetMenuOrder}, then assign contiguous
 * `widgetMenuOrder` values `0..n-1` (stable for ties).
 */
export function renumberPatientDashboardWidgetMenuOrder(
  widgets: mumms.WidgetConfig[],
): mumms.WidgetConfig[] {
  const sorted = [...widgets].sort(compareWidgetMenuOrder);
  return sorted.map((w, index) => ({
    ...w,
    widgetMenuOrder: index,
  }));
}

/**
 * Map a config-service widget row to {@link mumms.WidgetConfig}.
 *
 * **Why this exists:** `GET /api/widgets/config` returns rows shaped as
 * {@link mumms.WidgetConfigApiItem}. The dashboard grid, print UI, and widget
 * menu all consume {@link mumms.WidgetConfig} — parsed objects with no
 * config-service metadata.
 *
 * **JSON string fields:** The API persists `printConfig` and `idgConfig` as JSON
 * strings (e.g. `'{"options":{...}}'`, `'{"order":"0"}'`). Dashboard code
 * expects objects: `PrintInterface` reads `printConfig.options` / `checkboxes`,
 * and IDG meeting mode reads `idgConfig.order` on the grid. Invalid JSON becomes
 * `null` so callers can treat missing print/IDG options safely.
 *
 * **Dropped API-only fields:** `readOnly` on the API row is not used by the
 * dashboard (chart read-only comes from user/patient; widget watermark uses
 * `watermark.readOnly`). `groupType`, `groupId`, and `dashboard` are
 * config-service bookkeeping and are not part of {@link mumms.WidgetConfig}.
 *
 * **Stable identity:** `componentName` (e.g. `Manifest`, `ClearScripts`) is the
 * registry key for React widgets and print preconfig. `widgetName` is display
 * only (e.g. "The Manifest", "Medications - ClearScripts") and must not be used
 * for matching or deduplication.
 */
export function normalizeApiWidget(
  raw: mumms.WidgetConfigApiItem,
): mumms.WidgetConfig {
  const printConfig = parseApiJsonField<
    NonNullable<mumms.WidgetConfig['printConfig']>
  >(raw.printConfig ?? undefined);

  const idgConfig = parseApiJsonField<mumms.IDGMeetingConfig>(
    raw.idgConfig ?? undefined,
  );

  return {
    widgetName: raw.widgetName,
    componentName: raw.componentName,
    widgetMenuOrder: raw.widgetMenuOrder,
    printable: raw.printable,
    idgConfig,
    hasPrintRange: raw.hasPrintRange,
    printMenuOrder: raw.printMenuOrder,
    printConfig,
    beta: raw.beta,
    expandable: raw.expandable,
    watermark: raw.watermark,
    doNotShow: raw.doNotShow,
    showForBins: raw.showForBins,
    hideForBins: raw.hideForBins,
  };
}

/**
 * Normalize layout widgets and assign contiguous menu order.
 * Accepts config-service rows or env {@link mumms.WidgetConfig} (fallback).
 */
export function resolveLayoutWidgets(
  layout: WidgetConfigSource[] | undefined,
): mumms.WidgetConfig[] {
  if (!layout?.length) {
    return [];
  }
  return renumberPatientDashboardWidgetMenuOrder(layout.map(toWidgetConfig));
}

/**
 * Palette widgets the user can add — not already on the layout.
 *
 * Config-service returns `palette` as the full set of widgets available to the
 * user (often including every layout row again). This function returns palette
 * entries whose {@link mumms.WidgetConfig.componentName} is **not** on
 * `layout`, which drives the patient dashboard add-widget menu in App.
 *
 * **Match on `componentName`, not `widgetName`:** Display titles differ from
 * registry keys (`widgetName` "The Manifest" → `componentName` `Manifest`;
 * "Medications - ClearScripts" → `ClearScripts`). The grid, print builders, and
 * add-widget dedupe in Dashboard all key off `componentName`. Matching on
 * `widgetName` would fail to exclude layout tiles or would treat the same
 * widget as two different entries.
 *
 * **Preserve palette array order — do not sort by `widgetMenuOrder`:** Layout
 * rows are renumbered in {@link resolveLayoutWidgets}; palette rows are not.
 * The API palette order is the intended add-menu order. Sorting palette by
 * `widgetMenuOrder` would reorder entries incorrectly — e.g. `ClearScripts`
 * (`widgetMenuOrder` 2) before `Manifest` (`widgetMenuOrder` 3) even when the
 * API lists Manifest first — and would regress the ordering bugs we fixed when
 * role lists were client-side. Keep the server palette sequence as returned.
 */
export function resolvePaletteWidgets(
  palette: WidgetConfigSource[] | undefined,
  layout: WidgetConfigSource[] | undefined,
): mumms.WidgetConfig[] {
  if (!palette?.length) {
    return [];
  }

  const layoutNames = layoutComponentNames(layout);

  return palette
    .filter((w) => w.componentName && !layoutNames.has(w.componentName))
    .map(toWidgetConfig);
}
